import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';

import CaptureCard from 'containers/admin/clients/client/tabs-components/critical-populations/components/capture-card';
import CaptureListSkeleton from 'containers/admin/clients/client/tabs-components/critical-populations/components/capture-list-skeleton';
import {
  EMPTY_STATE_CLASS,
  EMPTY_STATE_IMAGE_CLASS,
  EMPTY_STATE_TEXT_CLASS,
  EMPTY_TEXT,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import { getCardOpenAction } from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.helpers';
import type {
  CaptureListTab,
  DecisionActionKey,
  DeleteTarget,
  OpenDecisionPanel,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';

import type { CriticalPopulationResponse } from 'api/generated/schemas';

import Icons from 'assets/icons';

interface CaptureListProps {
  tab: CaptureListTab;
  rows: CriticalPopulationResponse[];
  clientId: number;
  monitoringPendingIds?: ReadonlySet<number>;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  openPanel: OpenDecisionPanel | null;
  onEdit: (id: number) => void;
  onDelete: (target: DeleteTarget) => void;
  onOpenDecision: (id: number, action: DecisionActionKey) => void;
  onCloseDecision: () => void;
  onDecisionDone: () => void;
}

const CaptureList = ({
  tab,
  rows,
  clientId,
  monitoringPendingIds,
  isLoading,
  isError,
  onRetry,
  openPanel,
  onEdit,
  onDelete,
  onOpenDecision,
  onCloseDecision,
  onDecisionDone,
}: CaptureListProps) => {
  if (isLoading) return <CaptureListSkeleton tab={tab} />;

  if (isError) {
    return (
      <Stack
        role="alert"
        sx={{
          alignItems: 'center',
          gap: 1.5,
          borderRadius: '0.375rem',
          bgcolor: 'tokens.navy.50',
          p: 4,
          textAlign: 'center',
        }}
        data-testid="capture-list-error"
      >
        <p className="text-sm text-gray-700">
          Couldn&apos;t load Critical Elements.
        </p>
        <Button variant="neutral-outlined" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </Stack>
    );
  }

  if (!rows.length) {
    return (
      <EmptySpaceContent
        image={<Icons.EmptyImage customClass={EMPTY_STATE_IMAGE_CLASS} />}
        text={EMPTY_TEXT[tab]}
        customClass={EMPTY_STATE_CLASS}
        textClass={EMPTY_STATE_TEXT_CLASS}
      />
    );
  }

  return (
    <Stack
      component="ul"
      sx={[{ gap: 1.5 }, tab !== 'evaluating' && { mt: 0.5 }]}
      data-testid={`capture-list-${tab}`}
    >
      {rows.map((row) => (
        <CaptureCard
          key={row.id}
          row={row}
          tab={tab}
          clientId={clientId}
          showMonitoringAlert={
            row.id !== undefined && !!monitoringPendingIds?.has(row.id)
          }
          openAction={getCardOpenAction(openPanel, row.id, tab)}
          onEdit={onEdit}
          onDelete={onDelete}
          onOpenDecision={onOpenDecision}
          onCloseDecision={onCloseDecision}
          onDecisionDone={onDecisionDone}
        />
      ))}
    </Stack>
  );
};

export default CaptureList;
